import * as React from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Sheet from '@mui/joy/Sheet';
import { Bot, BotMode, BotStatsInfo } from '../../../models/bot';
import { botStatsApi, botsListApi } from '../botsApi';
import { NotificationService } from '../../../common/notification';
import { FilterBar } from '../../dashboard/components';
import { toInputValue, subtractMonths } from '../../dashboard/utils/date';
import { TimeGranularity } from '../../../models/paymentSummary';
import { aggregateBotStats, DEFAULT_TREND_N } from './stats/aggregate';
import { StatsKpi } from './stats/StatsKpi';
import { LoadShareBar } from './stats/LoadShareBar';
import { BotsStatsTable } from './stats/BotsStatsTable';
import { SelectedBotsTrend } from './stats/SelectedBotsTrend';
import { AnomaliesList } from './stats/AnomaliesList';

const EMPTY_STATS: BotStatsInfo = {
    statsDataPoints: [],
    totalSuccesses: 0,
    totalFailures: 0,
    totalSuccessesPrevious: 0,
    totalFailuresPrevious: 0,
};

export default function Stats() {
    const [fromDate, setFromDate] = React.useState<string>(() => {
        const now = new Date();
        const from = subtractMonths(now, 1);
        return toInputValue(from);
    });
    const [toDate, setToDate] = React.useState<string>(() => toInputValue(new Date()));
    const [granularity, setGranularity] = React.useState<TimeGranularity>(TimeGranularity.Day);

    const [loading, setLoading] = React.useState(false);
    const [stats, setStats] = React.useState<BotStatsInfo>(EMPTY_STATS);
    const [bots, setBots] = React.useState<Bot[]>([]);
    const [selectedIds, setSelectedIds] = React.useState<number[]>([]);
    const userTouchedSelection = React.useRef(false);

    React.useEffect(() => {
        loadData().catch(console.error);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromDate, toDate, granularity]);

    const aggregated = React.useMemo(
        () =>
            aggregateBotStats({
                points: stats.statsDataPoints,
                bots,
                fromDate,
                toDate,
                granularity,
            }),
        [stats, bots, fromDate, toDate, granularity],
    );

    React.useEffect(() => {
        const activeIds = aggregated.rows.filter((r) => r.total > 0).map((r) => r.botId);
        const fallback = (activeIds.length > 0 ? activeIds : aggregated.rows.map((r) => r.botId))
            .slice(0, DEFAULT_TREND_N);

        if (!userTouchedSelection.current) {
            setSelectedIds(fallback);
            return;
        }

        setSelectedIds((prev) => {
            const allowed = new Set(aggregated.rows.map((r) => r.botId));
            const next = prev.filter((id) => allowed.has(id));
            return next.length > 0 ? next : fallback;
        });
    }, [aggregated]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [statsResult, botsResult] = await Promise.all([
                botStatsApi(undefined, {
                    fromUtc: fromDate,
                    toUtc: toDate,
                    granularity,
                }),
                botsListApi({ page: 1, count: 500, mode: [BotMode.TF2] }),
            ]);
            if (statsResult.result) {
                setStats(statsResult.value ?? EMPTY_STATS);
                userTouchedSelection.current = false;
            } else {
                NotificationService.alertError(
                    'Не удалось загрузить статистику. Пожалуйста попробуйте позже или обратитесь в службу поддержки',
                );
            }
            if (botsResult.result && botsResult.value?.items) {
                setBots(botsResult.value.items);
            }
        } finally {
            setLoading(false);
        }
    };

    const toggleSelected = React.useCallback((botId: number) => {
        userTouchedSelection.current = true;
        setSelectedIds((prev) => {
            if (prev.includes(botId)) {
                return prev.filter((id) => id !== botId);
            }
            return [...prev, botId];
        });
    }, []);

    const setSelectedFromTable = React.useCallback((ids: number[]) => {
        userTouchedSelection.current = true;
        setSelectedIds(ids);
    }, []);

    const selectBotForTrend = React.useCallback((botId: number) => {
        userTouchedSelection.current = true;
        setSelectedIds((prev) => {
            if (prev.includes(botId)) {
                return prev;
            }
            return [...prev, botId];
        });
    }, []);

    const kpiTotals = React.useMemo(() => {
        let successes = 0;
        let failures = 0;
        for (const row of aggregated.rows) {
            successes += row.successes;
            failures += row.failures;
        }
        return { successes, failures };
    }, [aggregated]);

    const hasChartData = aggregated.grandTotal > 0;

    return (
        <React.Fragment>
            <FilterBar
                loading={loading}
                onApplyRange={(from, to) => {
                    setFromDate(from);
                    setToDate(to);
                }}
            />

            <StatsKpi
                totalSuccesses={kpiTotals.successes}
                totalFailures={kpiTotals.failures}
                totalSuccessesPrevious={stats.totalSuccessesPrevious}
                totalFailuresPrevious={stats.totalFailuresPrevious}
            />

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    mt: 2,
                    mb: 1.5,
                    flexWrap: 'wrap',
                }}
            >
                <FormControl size="sm" sx={{ minWidth: 160 }}>
                    <FormLabel>Группировка</FormLabel>
                    <Select
                        value={granularity}
                        onChange={(_, value) =>
                            value != null && setGranularity(value as TimeGranularity)
                        }
                    >
                        <Option value={TimeGranularity.Hour}>По часам</Option>
                        <Option value={TimeGranularity.Day}>По дням</Option>
                        <Option value={TimeGranularity.Month}>По месяцам</Option>
                    </Select>
                </FormControl>
            </Box>

            {!hasChartData ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <Typography level="body-sm" color="neutral">
                        Нет данных для отображения
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Sheet variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
                        <LoadShareBar
                            rows={aggregated.rows}
                            grandTotal={aggregated.grandTotal}
                        />
                    </Sheet>
                    <Sheet variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
                        <AnomaliesList
                            anomalies={aggregated.anomalies}
                            selectedIds={selectedIds}
                            onSelectBot={selectBotForTrend}
                        />
                    </Sheet>
                    <Sheet variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
                        <SelectedBotsTrend
                            timeRows={aggregated.timeRows}
                            rows={aggregated.rows}
                            selectedIds={selectedIds}
                            colors={aggregated.colors}
                            granularity={granularity}
                        />
                    </Sheet>
                    <Sheet variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
                        <BotsStatsTable
                            rows={aggregated.rows}
                            selectedIds={selectedIds}
                            colors={aggregated.colors}
                            onToggle={toggleSelected}
                            onSetSelected={setSelectedFromTable}
                        />
                    </Sheet>
                </Box>
            )}
        </React.Fragment>
    );
}
